"use client";

import React, { useEffect, useState } from "react";
import { PagePayoutSchema } from "@/sanity/pages/PagePayoutTemplate";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";

import laurels from "@/public/payout/laurels.svg";
import ipad from "@/public/global/ipad.png";
import { PageAssetsSchema } from "@/sanity/pages/PageAssetsTemplate";
import { motion } from "framer-motion";
import ChipSerif from "@/components/global/ui/ChipSerif";
import bulletIcon from "@/public/assets/bulletIcon.svg";

import PricingTableAssets from "./PricingTableAssets";

export default function PlanSection({
  data,
  className,
}: {
  data: PageAssetsSchema;
  className?: string;
}) {
  return (
    <section className={cn("relative overflow-clip lg:pb-[8rem]", className)}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn("h-full border-l border-l-[#E5E5E5] hidden lg:block")}
          />
        ))}
      </div>

      <div className="container max-w-none w-[min(230.5rem,100%)] px-[2rem] flex flex-col items-start lg:items-center pb-[8rem] lg:py-[10rem] lg:px-[15rem] relative bg-white lg:border lg:border-black/10 lg:rounded-[5rem]">
        {data?.planChip && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <ChipSerif
              text={data.planChip}
              className="text-black/70 mb-[1.5rem]"
            />
          </motion.div>
        )}

        <motion.div
          className="mb-[4rem] lg:mb-[5rem] richtext richtext-start lg:richtext-center
          prose-headings:text-[#103037] prose-headings:tracking-normal [&_span]:text-[#103037] [&_span]:font-instrument-sans [&_span]:font-semibold lg:[&_span]:font-bold lg:[&_span]:text-[#6DA991] lg:[&_span]:font-gestura
          prose-p:text-black/70 prose-p:text-[2.25rem] prose-p:w-[min(56.5rem,100%)] prose-strong:text-white [text-wrap-style:pretty]"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {data?.planText && (
            <PortableText
              value={data?.planText}
              components={richTextComponent}
            />
          )}
        </motion.div>

        <PricingTableAssets
          lang={"en"}
          planPricing={data.planPricing}
          pageType={data.planPageType}
        />
      </div>
    </section>
  );
}
